// src/components/FAQ.jsx
// FAQ-Komponente mit Schema.org JSON-LD für Rich Snippets bei Google
import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * FAQ-Komponente mit automatischem Schema.org FAQPage Markup
 * 
 * @param {Object} props
 * @param {Array} props.items - Array von {question: string, answer: string}
 * @param {string} props.title - Optionaler Titel über der FAQ-Sektion
 * @param {string} props.subtitle - Optionaler Untertitel
 * @param {string} props.schemaId - Eindeutige ID für das Schema-Script (default: "faq-schema")
 */
export default function FAQ({ items = [], title, subtitle, schemaId = "faq-schema" }) {
    const [openIndex, setOpenIndex] = useState(null);

    // Schema.org FAQPage JSON-LD ins <head> injizieren
    useEffect(() => {
        if (!items.length) return;

        const schema = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": items.map(item => ({
                "@type": "Question",
                "name": item.question,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": item.answer.replace(/<[^>]*>/g, '') // HTML-Tags für Schema entfernen
                }
            }))
        };

        // Altes Script entfernen falls vorhanden
        const existing = document.getElementById(schemaId);
        if (existing) existing.remove();

        // Neues Script einfügen
        const script = document.createElement("script");
        script.id = schemaId;
        script.type = "application/ld+json";
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);

        return () => {
            const el = document.getElementById(schemaId);
            if (el) el.remove();
        };
    }, [items, schemaId]);

    if (!items.length) return null;

    return (
        <section className="mt-16">
            {title && (
                <div className="text-center mb-8">
                    <h2 className="text-2xl sm:text-3xl font-heading font-black uppercase tracking-tight text-slate-900 dark:text-white">
                        {title}
                    </h2>
                    {subtitle && (
                        <p className="mt-2 text-slate-500 dark:text-slate-400 font-medium italic">
                            {subtitle}
                        </p>
                    )}
                </div>
            )}

            <div className="rounded-2xl border border-slate-200/60 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden shadow-sm">
                {items.map((item, i) => {
                    const isOpen = openIndex === i;
                    return (
                        <div key={i} className="group">
                            <button
                                onClick={() => setOpenIndex(isOpen ? null : i)}
                                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                aria-expanded={isOpen}
                            >
                                <span className="font-semibold text-slate-900 dark:text-slate-100 pr-4">
                                    {item.question}
                                </span>
                                <ChevronDown 
                                    className={`h-5 w-5 text-slate-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
                                />
                            </button>
                            
                            <div
                                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                    isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                                }`}
                            >
                                <div className="px-6 pb-5 pt-0">
                                    {/* Falls answer HTML enthält, als HTML rendern */}
                                    {/<[a-z][\s\S]*>/i.test(item.answer) ? (
                                        <div 
                                            className="text-slate-600 dark:text-slate-400 leading-relaxed prose prose-sm dark:prose-invert max-w-none"
                                            dangerouslySetInnerHTML={{ __html: item.answer }}
                                        />
                                    ) : (
                                        <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                                            {item.answer}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
